// External Dependencies
import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ActiveIcon from '@mui/icons-material/CheckCircle';
import InactiveIcon from '@mui/icons-material/NotInterested';
import type { ApolloError } from '@apollo/client';

// Internal Dependencies
import {
  Flex,
  FullWidthDiv,
  Subtitle,
} from 'components/shared';
import type { FeatureFlag } from 'gql/queries/featureFlags';

// Local Dependencies
import CreateOverrideForm from './CreateOverrideForm';
import FlagListSecondaryAction from './FlagListSecondaryAction';

// Local Typings
interface Props {
  flag: FeatureFlag;
  onError: (error: ApolloError | null) => void;
}

// Component Definition
const OverridesList = ({
  flag,
  onError,
}: Props): JSX.Element => {
  const [deleteWarnId, setDeleteWarnId] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleToggleForm = useCallback(() => setIsFormOpen((open) => !open), []);

  const handleSuccess = useCallback(() => {
    handleToggleForm();
    onError(null);
  }, [handleToggleForm, onError]);

  return (
    <FullWidthDiv>
      <>
        <Box my={3}>
          <Subtitle>
            Organization Overrides
          </Subtitle>
        </Box>

        {flag.overrides.length ? (
          <List component={Paper}>
            {flag.overrides.map((override) => (
              <ListItem key={override.id}>
                <ListItemIcon>
                  {override.isActive
                    ? <ActiveIcon color="primary" />
                    : <InactiveIcon color="error" />}
                </ListItemIcon>

                <ListItemText
                  primary={override.organization.label}
                  secondary={override.reason}
                />

                <FlagListSecondaryAction
                  deleteWarnId={deleteWarnId}
                  onError={onError}
                  override={override}
                  setDeleteWarnId={setDeleteWarnId}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography
            sx={{ marginTop: 2 }}
            variant="body2"
          >
            No overrides set for this feature flag.
          </Typography>
        )}

        {isFormOpen ? (
          <CreateOverrideForm
            existingOverrideOrgIds={flag.overrides.map((override) => override.organization.id)}
            featureFlagId={flag.id}
            flagLabel={flag.label}
            onCancel={handleToggleForm}
            onError={onError}
            onSuccess={handleSuccess}
          />
        ) : (
          <Flex justifyContent="flex-end">
            <Button
              color="primary"
              onClick={handleToggleForm}
              sx={{ marginTop: 2 }}
              variant="contained"
            >
              Add Override
            </Button>
          </Flex>
        )}
      </>
    </FullWidthDiv>
  );
};

export default OverridesList;
